'use client'

import { Notification, NotificationType } from '@/lib/api/notifications'
import { useNotifications } from '@/lib/hooks/use-notifications'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
    MdCampaign,
    MdCheck,
    MdCheckCircle,
    MdDelete,
    MdError,
    MdInfo,
    MdNotifications,
    MdSettings,
    MdWarning,
} from 'react-icons/md'
import { Link, useNavigate } from 'react-router-dom'

const NOTIFICATION_ICONS: Record<NotificationType, React.ReactNode> = {
    INFO: <MdInfo className="text-info" size={18} />,
    SUCCESS: <MdCheckCircle className="text-success" size={18} />,
    WARNING: <MdWarning className="text-warning" size={18} />,
    ERROR: <MdError className="text-error" size={18} />,
    SYSTEM: <MdSettings className="text-secondary" size={18} />,
    ANNOUNCEMENT: <MdCampaign className="text-secondary" size={18} />,
}

export function NotificationDropdown() {
    const navigate = useNavigate()
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
    } = useNotifications()

    const closeDropdown = () => {
        const dropdown = document.activeElement as HTMLElement
        if (dropdown) dropdown.blur()
    }

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.isRead) {
            markAsRead(notification.id).catch(() => {})
        }
        closeDropdown()
        if (notification.link) {
            navigate(notification.link)
        }
    }

    const handleMarkAllAsRead = async () => {
        await markAllAsRead()
    }

    const handleDelete = async (
        e: React.MouseEvent,
        notificationId: number
    ) => {
        e.stopPropagation()
        e.preventDefault()
        await deleteNotification(notificationId)
    }

    const formatDate = (dateString: string) => {
        try {
            return formatDistanceToNow(new Date(dateString), {
                addSuffix: true,
                locale: ptBR,
            })
        } catch {
            return ''
        }
    }

    const displayedNotifications = notifications.slice(0, 5)

    return (
        <div className="dropdown dropdown-bottom dropdown-end max-sm:!static">
            <div
                tabIndex={0}
                role="button"
                className="btn btn-ghost btn-circle relative"
                aria-label="Notificações"
            >
                <MdNotifications size={24} />
                {unreadCount > 0 && (
                    <span className="badge badge-error badge-xs absolute -top-1 -right-1 text-white font-bold min-w-[18px] h-[18px]">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </div>

            <ul
                tabIndex={0}
                className="dropdown-content menu bg-base-100 rounded-box z-[100] w-96 max-sm:!inset-x-2 max-sm:!w-auto p-0 shadow-lg border border-base-200 mt-2"
            >
                <li className="px-4 py-3 border-b border-base-300">
                    <div className="flex items-center justify-between p-0 hover:bg-transparent cursor-default">
                        <span className="font-semibold text-base">
                            Notificações
                        </span>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllAsRead}
                                className="btn btn-ghost btn-xs gap-1"
                                title="Marcar todas como lidas"
                            >
                                <MdCheck size={14} />
                                Marcar todas
                            </button>
                        )}
                    </div>
                </li>

                <div className="overflow-y-auto max-h-[55vh]">
                    {loading ? (
                        <li className="py-8">
                            <div className="flex items-center justify-center hover:bg-transparent cursor-default">
                                <span className="loading loading-spinner loading-md"></span>
                            </div>
                        </li>
                    ) : displayedNotifications.length === 0 ? (
                        <li className="py-8">
                            <div className="flex flex-col items-center justify-center text-base-content/60 hover:bg-transparent cursor-default">
                                <MdNotifications
                                    size={40}
                                    className="mb-2 opacity-40"
                                />
                                <p className="text-sm">Nenhuma notificação</p>
                            </div>
                        </li>
                    ) : (
                        displayedNotifications.map(notification => (
                            <li key={notification.id}>
                                <div
                                    className={`flex gap-3 px-4 py-3 hover:bg-base-200 cursor-pointer transition-colors rounded-none  ${
                                        !notification.isRead
                                            ? 'bg-base-200/50 '
                                            : ''
                                    }`}
                                    onClick={() =>
                                        handleNotificationClick(notification)
                                    }
                                >
                                    <div className="flex-shrink-0 mt-0.5">
                                        {NOTIFICATION_ICONS[notification.type]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <p
                                                className={`text-sm font-medium truncate ${
                                                    !notification.isRead
                                                        ? 'text-base-content'
                                                        : 'text-base-content/70'
                                                }`}
                                            >
                                                {notification.title}
                                            </p>
                                            <button
                                                onClick={e =>
                                                    handleDelete(
                                                        e,
                                                        notification.id
                                                    )
                                                }
                                                className="btn btn-ghost btn-xs p-1 opacity-40 hover:opacity-100"
                                                title="Excluir"
                                            >
                                                <MdDelete size={14} />
                                            </button>
                                        </div>
                                        <p className="text-xs text-base-content/60 line-clamp-2 mt-0.5">
                                            {notification.message}
                                        </p>
                                        <div className="flex items-center mt-1">
                                            <span className="text-xs text-base-content/50">
                                                {formatDate(
                                                    notification.createdAt
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))
                    )}
                </div>

                <li className="border-t border-base-300 p-2">
                    <Link
                        to="/notifications"
                        className="btn btn-ghost btn-sm w-full justify-center"
                        onClick={closeDropdown}
                    >
                        Ver todas
                    </Link>
                </li>
            </ul>
        </div>
    )
}

export default NotificationDropdown
